import React, { useState, useCallback } from 'react'
import dayjs from 'dayjs'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers-pro'

import MuiModal from '../shared/mui_modal';
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material"

import { devices } from '../../ajax_clients';
import { flashError } from '../../shared/functions';

export default function ComplianceOverrideModal({
  onClose,
  onSave,
  deviceId,
  complianceOverride,
  overrideExpiresAt,
}) {
  const [overrideStatus, setOverrideStatus] = useState(complianceOverride || "none")
  const [expiresAt, setExpiresAt] = useState(overrideExpiresAt ? dayjs(overrideExpiresAt) : null)
  const [noExpiration, setNoExpiration] = useState(!overrideExpiresAt)
  const [pickerOpen, setPickerOpen] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  const handleOverrideChange = useCallback((event) => {
    setOverrideStatus(event.target.value)
  }, [])

  const handleNoExpirationChange = useCallback((event) => {
    setNoExpiration(event.target.checked)
  }, [])

  const handleCommit = useCallback(() => {
    let overrideExpiresAtValue = null
    if (overrideStatus !== 'none' && !noExpiration) {
      overrideExpiresAtValue = expiresAt?.toISOString() || null
    }
    setIsSaving(true)
    devices.updateCompliance(deviceId, {
      complianceOverride: overrideStatus,
      overrideExpiresAt: overrideExpiresAtValue,
    }).then((data) => {
      onSave(data)
      onClose()
    }).catch(({ responseJSON }) => {
      flashError(responseJSON, "Failed to save compliance override.")
    }).always(() => {
      setIsSaving(false)
    })
  }, [deviceId, overrideStatus, expiresAt, noExpiration, onSave, onClose])

  return (
    <MuiModal
      titleText="Compliance Override"
      buttonText={isSaving ? "Saving..." : "Save"}
      commitDisabled={isSaving || (overrideStatus !== 'none' && !noExpiration && !expiresAt)}
      onClose={onClose}
      onCommit={handleCommit}
    >
      <Grid
        container
        direction="column"
        spacing={3}
        sx={{ minWidth: "400px" }}
      >
        <Grid item container alignItems="center" spacing={2}>
          <Grid item xs={4}>
            <InputLabel id="override-status-label">Override Status</InputLabel>
          </Grid>
          <Grid item xs={8}>
            <FormControl fullWidth>
              <Select
                labelId="override-status-label"
                id="override-status"
                value={overrideStatus}
                onChange={handleOverrideChange}
                variant="standard"
                disabled={isSaving}
              >
                <MenuItem value="none">None</MenuItem>
                <MenuItem value="override_compliant">Compliant</MenuItem>
                <MenuItem value="override_notCompliant">Not Compliant</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {overrideStatus !== 'none' && (
          <Grid item container alignItems="flex-start" spacing={2}>
            <Grid item xs={4}>
              <InputLabel sx={{ pt: 1 }}>Override Expiration</InputLabel>
            </Grid>
            <Grid item xs={8}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={noExpiration}
                    onChange={handleNoExpirationChange}
                    size="small"
                    disabled={isSaving}
                  />
                }
                label="No expiration"
              />
              {!noExpiration && (
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DateTimePicker
                    value={expiresAt}
                    onChange={setExpiresAt}
                    open={pickerOpen}
                    onOpen={() => setPickerOpen(true)}
                    onClose={() => setPickerOpen(false)}
                    disabled={isSaving}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="standard"
                        fullWidth
                        onClick={() => setPickerOpen(true)}
                        inputProps={{ ...params.inputProps, readOnly: true }}
                      />
                    )}
                  />
                </LocalizationProvider>
              )}
            </Grid>
          </Grid>
        )}
      </Grid>
    </MuiModal>
  );
}
